import React, {useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCheckCircle,
  FiMapPin,
  FiUser,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { FaUserTie } from "react-icons/fa"
import { MdLocationOn } from "react-icons/md";
import { MARK_COMPLETE, VIEW_DAY } from "../../../routes";
import { formatTime } from "../../../Utils/timeUtlil";
import { STATUS } from "../../../Constants/Strings";
import { STATUS_DISPLAY } from "../constants";

const TaskMilestoneSection = ({
  tasks,
  expandedTasks = [],
  showIcons = false,
  onToggle,
}) => {
  const themeColor = "text-primary";
  const navigate = useNavigate();
  const sortedTasks = [...tasks].reverse()

  const [mapExpandedTaskIds, setMapExpandedTaskIds] = useState([]);
  const toggleMapForTask = (taskId) => {
    setMapExpandedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };


  if (!tasks || tasks.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl py-4 pr-4 w-full">
      <h2 className="text-2xl font-bold text-primary mb-6 border-b pb-2 flex items-center gap-2">
        <span className={themeColor}></span> Today's Tasks
      </h2>

      <div className="max-h-[60vh] overflow-y-auto pr-1 relative">
        {!tasks || tasks.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">
            No tasks available
          </p>
        ) : (
          sortedTasks.map((task, index) => {
            const isExpanded = expandedTasks.includes(task.id);

            return (
              <div className="flex items-stretch relative" key={task.id}>
                {/* Left timeline column */}
                <div className="w-12 flex flex-col items-center relative">
                  {/* Full-height vertical line behind circle */}
                  {index !== tasks.length - 1 && (
                    <div className="absolute top-6 bottom-0 w-px bg-primary z-0" />
                  )}
                  {/* Circle on top of the line */}
                  <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold z-10">
                    {tasks.length - index}
                  </div>
                </div>

                {/* Right task card */}
                <div
                  className="pt-2 p-4 shadow-[0_2px_4px_rgba(0,0,0,0.08)] flex-1 ml-2 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() =>
                    navigate(
                      `${VIEW_DAY.replace(":dayId", task.day)}#task-${task.id}`
                    )
                  }
                >
                  <div className="text-md font-semibold text-gray-900 truncate">
                    {task.task_title}
                  </div>
                  <div className="text-gray-600 text-sm mb-1">
                    {formatTime(task.start_time)}
                    {task.end_time && ` - ${formatTime(task.end_time)}`}
                  </div>
                  <div className="flex justify-between items-center">
                    <span
                      className={`text-xs font-semibold px-3 py-1 rounded-full 
                      ${
                        task.status === STATUS.COMPLETED
                          ? "bg-green-100 text-green-700"
                          : task.status === STATUS.IN_PROGRESS
                          ? "bg-yellow-100 text-yellow-800"
                          : task.status === STATUS.COMPLETED_BY_ADMIN
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600"
                      }
                    `}
                    >
                      {task.status === STATUS.IN_PROGRESS
                        ? STATUS_DISPLAY.IN_PROGRESS
                        : task.status === STATUS.COMPLETED
                        ? STATUS_DISPLAY.COMPLETED
                        : STATUS_DISPLAY.COMPLETED_BY_ADMIN
                      }
                    </span>
                    {task.status === STATUS.IN_PROGRESS && (
                      <button
                        className="text-sm p-1 rounded-xl bg-blue-200 text-primary hover:text-hoverColor transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(MARK_COMPLETE, { state: { task } });
                        }}
                      >
                        <FiCheckCircle size={20} />
                      </button>
                    )}
                    {showIcons && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card navigation
                          onToggle?.(task.id);
                        }}
                      >
                        {isExpanded ? (
                          <FiChevronUp size={20} className="text-primary" />
                        ) : (
                          <FiChevronDown size={20} className="text-primary" />
                        )}
                      </button>
                    )}
                  </div>
                  {showIcons && isExpanded && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-800 text-sm">
                      <p className="flex items-center gap-2">
                        <FiUser className="text-primary" />
                        {task.created_by || "-"}
                      </p>

                      <p className="flex items-center gap-2">
                        <FaUserTie className="text-primary" />
                        <span className="capitalize">
                          {task.client_name || "-"}
                        </span>
                      </p>

                      <p className="flex items-center gap-2">
                        <MdLocationOn className="text-primary" />
                        {task.client_address || "-"}
                      </p>

                      <div className="flex flex-col sm:col-span-1 gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMapForTask(task.id);
                          }}
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <FiMapPin className="text-primary" />
                          <span>
                            {mapExpandedTaskIds.includes(task.id)
                              ? "Hide Map"
                              : "View Map"}
                          </span>
                        </button>

                        {mapExpandedTaskIds.includes(task.id) &&
                          task.latitude &&
                          task.longitude && (
                            <div className="mt-2 w-full h-64 rounded overflow-hidden border shadow-sm">
                              <iframe
                                src={`https://www.google.com/maps?q=${task.latitude},${task.longitude}&output=embed`}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Display Map Inframe"
                              />
                            </div>
                          )}

                        {!task.latitude && !task.longitude && (
                          <span className="text-gray-400 ml-6">
                            Unavailable
                          </span>
                        )}
                      </div>

                      {task.task_description && (
                        <div className="mt-2 sm:col-span-2 text-sm text-gray-700 border-t pt-3">
                          <span className="font-bold block mb-1">
                            Description:
                          </span>
                          <p className="leading-relaxed text-justify">
                            {task.task_description}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TaskMilestoneSection;
